import axios from "axios";
import Cookies from "universal-cookie";
import store from "../redux/store";
import { dismissTeamConflict, updateLastLoadedPath, teamConflictReceived, teamSwitchStarted } from "../redux/currentTeamSlice";
import { currentPath, isRecordRead, matchesResource, recordRoute, refreshDestination, safeDestination } from "./routes";

export const TEAM_EVENT_KEY = "karmabox:team-context:v1";
let routerHistory;

export function handledTeamContextError(message = "Team context changed") {
  const error = new axios.Cancel(message);
  error.code = "TEAM_CONTEXT_HANDLED";
  return error;
}
export const isTeamContextHandled = error => error?.code === "TEAM_CONTEXT_HANDLED";

function isSamePage(path, location, resource) {
  const page = new URL(path, window.location.origin);
  return location.pathname === page.pathname && (resource
    ? matchesResource(location.pathname + location.search, resource)
    : location.search === page.search);
}

export function replaceLocation(path) {
  const destination = safeDestination(path);
  const url = new URL(destination, window.location.origin);
  if (url.pathname === window.location.pathname && url.search === window.location.search) {
    window.history.replaceState(null, "", destination);
    window.location.reload();
  } else window.location.replace(destination);
}

export function navigateToTeam(path, reload = true) {
  if (reload || !routerHistory) return replaceLocation(path);
  routerHistory.push(safeDestination(path));
}

export function notifyTeamChanged(teamId, userId) {
  try { localStorage.setItem(TEAM_EVENT_KEY, JSON.stringify({ userId, teamId, changedAt: Date.now() })); }
  catch (_) { /* Focus checks cover browsers where storage is unavailable. */ }
}

export function checkTeamContext(requestedTeamId) {
  const { current_user, current_team } = store.getState();
  if (!current_user.user.is_super_member) return true;
  if (current_team.teamSwitch && !current_team.teamSwitch.error) return false;
  const cookies = new Cookies();
  const teamId = cookies.get("team_id");
  const expectedTeamId = current_team.team.id || requestedTeamId;
  let destination;
  if (cookies.get("api_key") !== current_user.user.api_key) destination = "/login";
  else if (expectedTeamId && String(teamId || "") !== String(expectedTeamId)) {
    destination = teamId ? refreshDestination(currentPath()) : "/switch_acc";
  }
  if (destination) {
    store.dispatch(teamSwitchStarted({ teamId, destination }));
    replaceLocation(destination);
    return false;
  }
  return Boolean(current_team.team.id) || !teamId;
}

export function listenForTeamChanges(history) {
  routerHistory = history;
  const onStorage = event => {
    if (event.key !== TEAM_EVENT_KEY) return;
    try {
      if (JSON.parse(event.newValue)?.userId === store.getState().current_user.user.id) checkTeamContext();
    } catch (_) { /* Notifications only prompt a cookie check. */ }
  };
  const check = () => { if (document.visibilityState !== "hidden") checkTeamContext(); };
  const unlisten = history.listen(location => {
    const conflict = store.getState().current_team.teamConflict;
    if (!conflict || !isSamePage(conflict.path, location, recordRoute(conflict.path))) store.dispatch(dismissTeamConflict());
  });
  window.addEventListener("storage", onStorage);
  window.addEventListener("focus", check);
  window.addEventListener("pageshow", check);
  document.addEventListener("visibilitychange", check);
  return () => {
    unlisten();
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("focus", check);
    window.removeEventListener("pageshow", check);
    document.removeEventListener("visibilitychange", check);
    if (routerHistory === history) routerHistory = null;
  };
}

export function requestWithTeamContext(client, args) {
  const { current_user, current_team } = store.getState();
  if (!current_user.user.is_super_member || ["/users/validate", "/team_members/team", "/pipe_drive_app_init"].includes(args.url)) return client(args);
  if (!checkTeamContext()) return Promise.reject(handledTeamContextError());
  const userId = current_user.user.id, teamId = current_team.team.id;
  const apiKey = args.headers.Authorization, path = currentPath();
  const recordRequest = isRecordRead(args, path);
  const resource = recordRequest ? recordRoute(path) : null;
  // Hash changes keep the record mounted. Leaving and returning must still
  // invalidate this request, without introducing a global navigation counter.
  let leftPage = false;
  const stopNavigation = routerHistory?.listen(location => { if (!isSamePage(path, location, resource)) leftPage = true; });
  const navigationIsCurrent = () => !leftPage && isSamePage(path, routerHistory?.location || window.location, resource);
  const responseIsCurrent = () => {
    const state = store.getState();
    return state.current_user.user.is_super_member && state.current_user.user.id === userId && state.current_team.team.id === teamId &&
      checkTeamContext() && new Cookies().get("api_key") === apiKey && (!recordRequest || navigationIsCurrent());
  };
  return client(args).then(response => {
    if (!responseIsCurrent()) throw handledTeamContextError();
    if (recordRequest) store.dispatch(updateLastLoadedPath(currentPath()));
    return response;
  }, error => {
    if (!responseIsCurrent()) throw handledTeamContextError();
    const data = error.response?.data;
    if (error.response?.status === 409 && data?.code === "team_switch_required" &&
        String(data.request_team_id) === String(teamId) && String(data.request_team_id) === String(args.params.team_id) &&
        data.target_team?.id && data.target_team?.name && matchesResource(data.resource_path, data.resource)) {
      if (navigationIsCurrent()) store.dispatch(teamConflictReceived({ data, path: currentPath(), userId,
        cancelPath: safeDestination(current_team.lastLoadedPath, current_team.team.home_path || "/feed") }));
      throw handledTeamContextError("Team switch required");
    }
    throw error;
  }).finally(() => stopNavigation?.());
}
